import { MouseEvent, ReactNode, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import {
  BookOpenIcon,
  BriefcaseIcon,
  BuildingOfficeIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  MapIcon,
  UserCircleIcon,
  ExclamationCircleIcon,
  Cog8ToothIcon,
  UserIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  ReceiptPercentIcon,
} from '@heroicons/react/24/outline';

import { useAuth } from '@restworld/feature-authentication';

import { PartnersIcon, RestworldBlackIcon } from '../svg-icons';

export interface WorkerNavLinkType {
  id?: string;
  key?: string;
  url?: string;
  label: string;
  icon?: ReactNode;
  description?: string;
  subItems?: WorkerNavLinkType[];
  onClick?: (e?: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>) => void;
  openOnNewTab?: boolean;
  isNotVisible?: boolean;
  isRecommended?: boolean;
  isUnderlined?: boolean;
  locale?: string;
}
export type WorkerNavLinksType = {
  [UserType in 'employerType' | 'loggedIn' | 'nonLoggedIn' | 'ambassadorType' | 'leadType']: {
    primaryNav: WorkerNavLinkType[];
    secondaryNav: WorkerNavLinkType[];
  };
};

const commonProps = {
  className: 'h-5 w-5 text-black',
  'aria-hidden': true,
};
const useLoggedInAvatarItems = () => {
  const { t } = useTranslation();
  return useMemo(
    () =>
      [
        {
          key: 'profile',
          label: t('common:go_to_profile'),
          icon: <UserIcon {...commonProps} />,
          url: '/profilo',
        },
        {
          key: 'settings',
          label: t('common:settings'),
          icon: <Cog8ToothIcon {...commonProps} />,
          url: '/impostazioni',
          isUnderlined: true,
        },
        {
          key: 'assistance_desk',
          label: t('common:navigation.assistance_desk'),
          icon: <ExclamationCircleIcon {...commonProps} />,
          url: 'https://www.restworld.it/assistenza?per=lavoratori',
          openOnNewTab: true,
        },
        {
          key: 'open-intercom',
          label: 'Feedback',
          icon: <ChatBubbleOvalLeftEllipsisIcon {...commonProps} />,
          description: 'opens Intercom widget to let user chat the CS team',
        },
        {
          key: 'redirect-to-home',
          label: t('common:return_to_the_home'),
          icon: <ArrowLeftOnRectangleIcon {...commonProps} />,
          description: 'redirects to the home page',
          url: '/?redirected=true',
        },
        {
          key: 'logout',
          label: 'Logout',
          icon: <ArrowRightOnRectangleIcon {...commonProps} />,
          isNotVisible: true,
          description: 'Reset the auth status',
        },
      ] as WorkerNavLinkType[],
    [t]
  );
};
const useSocialResponsibilityItem = () => {
  const { t } = useTranslation();
  return useMemo(
    () =>
      ({
        key: 'social-responsibility',
        label: t('common:navigation.useful_resources'),
        icon: <BookOpenIcon {...commonProps} />,
        subItems: [
          {
            key: 'contact_us',
            url: '/contatti',
            label: t('common:contact_us'),
            locale: '',
          },
          {
            key: 'policy',
            url: '/privacy-policy',
            label: 'Privacy policy',
            locale: '',
          },
          {
            key: 'cookie',
            url: '/cookie-policy',
            label: 'Cookie policy',
            locale: '',
          },
          {
            key: 'faq',
            url: 'https://www.restworld.it/assistenza',
            label: 'FAQ',
          },
        ],
      } as WorkerNavLinkType),
    [t]
  );
};

const useEmployerPlatformItem = () => {
  const { t } = useTranslation();
  return useMemo(
    () =>
      ({
        key: 'employer-platform',
        label: t('common:who_we_are'),
        icon: <RestworldBlackIcon />,
        subItems: [
          {
            key: 'the-team',
            url: '/chi-siamo',
            label: t('common:navigation.the_team'),
            locale: '',
          },
          {
            key: 'social',
            url: '/responsabilita-sociale',
            label: t('common:social_responsibility'),
            locale: '',
          },
          {
            key: 'work_with_us',
            url: '/lavora-con-noi',
            label: t('common:work_with_us'),
            locale: '',
          },
          {
            key: 'become_a_partner',
            url: '/diventa-partner',
            label: t('common:become_a_partner'),
            locale: '',
          },
        ],
      } as WorkerNavLinkType),
    [t]
  );
};

const usePrimaryNavItems = () => {
  const socialResponsibilityItem = useSocialResponsibilityItem();
  const employerPlatformItem = useEmployerPlatformItem();
  const { user } = useAuth();
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        key: 'worker-platform',
        label: t('common:looking_for_job'),
        icon: <MagnifyingGlassIcon {...commonProps} />,
        subItems: [
          {
            key: 'looking-for-job',
            url: '/cerco-lavoro',
            label: t('common:work_offers'),
          },
          {
            key: 'seasonal_jobs',
            url: '/cerco-lavoro/stagionale',
            label: t('common:seasonal_jobs'),
          },
          {
            key: 'create_your_profile',
            url: '/candidati',
            label: t('common:navigation.create_your_profile'),
            isRecommended: true,
          },
          {
            key: 'faq',
            url: '/faq',
            label: t('common:frequent_questions'),
            locale: '',
          },
          {
            key: 'open-intercom',
            label: t('common:contact_us'),
            description: 'opens Intercom widget to let user chat the CS team',
          },
        ],
      },
      employerPlatformItem,
      socialResponsibilityItem,
      {
        key: 'who-we-are',
        label: t('common:for_companies'),
        icon: <BuildingOfficeIcon {...commonProps} />,
        subItems: [
          {
            key: 'appointment',
            url: 'https://www.restworld.it/aziende',
            label: t('common:make_an_appointment'),
            openOnNewTab: true,
          },
          {
            key: 'employer',
            url: '/employer',
            label: t('common:how_does_it_work'),
            locale: '',
          },
        ],
      },
      ...(user?.type === 'lead'
        ? [
            {
              key: 'settings',
              label: t('common:settings'),
              url: '/impostazioni',
            },
          ]
        : []),
    ],
    [employerPlatformItem, socialResponsibilityItem, t, user?.type]
  ) as WorkerNavLinkType[];
};
export const useNavLabelLinks = () => {
  const { t } = useTranslation();
  const primaryNavItems = usePrimaryNavItems();
  const loggedInAvatarItems = useLoggedInAvatarItems();
  const socialResponsibilityItem = useSocialResponsibilityItem();
  const employerPlatformItem = useEmployerPlatformItem();
  return useMemo(
    () => ({
      employerType: {
        primaryNav: [
          {
            key: 'your_positions',
            label: t('common:your_positions'),
            icon: <BriefcaseIcon {...commonProps} />,
            url: '/employer/job-positions',
          },
          {
            key: 'billing',
            label: t('common:billing'),
            icon: <ReceiptPercentIcon {...commonProps} />,
            url: '/employer/fatturazione',
          },
          {
            key: 'go_to_the_website',
            label: t('common:go_to_the_website'),
            icon: <HomeIcon {...commonProps} />,
            url: '/',
          },
        ],
        secondaryNav: [],
      },
      leadType: {
        primaryNav: primaryNavItems,
        secondaryNav: loggedInAvatarItems.filter((item) => item.key !== 'profile'),
      },
      ambassadorType: {
        primaryNav: [
          {
            key: 'location_map',
            url: '/cerco-lavoro',
            label: t('common:location_map'),
            icon: <MapIcon {...commonProps} />,
          },
          {
            key: 'candidature',
            url: '/challenge-wom/posizioni',
            label: t('common:list_of_positions'),
            icon: <BriefcaseIcon {...commonProps} />,
            locale: '',
          },
          {
            key: 'dashboard',
            url: '/challenge-wom/dashboard',
            label: 'Dashboard',
            icon: <HomeIcon {...commonProps} />,
            locale: '',
          },
          socialResponsibilityItem,
        ],
        // Empty since 18/09/2026: the ambassadors asked for the useful-links section to go
        // (Arianna Dani). Nothing rendered these anyway — `primaryItems`/`secondaryItems`
        // are declared in the overlay navs' prop types and never read in their bodies — but
        // leaving dead links here is how somebody later "restores" them.
        secondaryNav: [],
      },
      loggedIn: {
        primaryNav: [
          {
            key: 'profile',
            label: t('common:profile'),
            icon: <UserCircleIcon {...commonProps} />,
            url: '/profilo',
          },
          {
            key: 'explore_map',
            url: '/cerco-lavoro',
            label: t('common:explore_map'),
            icon: <MapIcon {...commonProps} />,
          },
          {
            key: 'your_applications',
            url: '/candidature',
            label: t('common:your_applications'),
            icon: <BriefcaseIcon {...commonProps} />,
          },
          {
            key: 'helpful-partners',
            url: '!#',
            label: t('common:helpful_partners'),
            icon: <PartnersIcon />,
            subItems: [
              {
                key: 'find_accommodation',
                label: t('common:find_accommodation'),
                url: 'https://housinganywhere.com/it/register/8474',
                openOnNewTab: true,
              },
              {
                key: 'training',
                label: t('common:training'),
                url: 'https://convivier.com/benvenuti-in-convivier',
              },
            ],
          },
          employerPlatformItem,
          socialResponsibilityItem,
          {
            key: 'settings',
            label: t('common:settings'),
            url: '/impostazioni',
          },
          {
            key: 'assistance',
            label: t('common:assistance'),
            url: 'https://www.restworld.it/assistenza?per=lavoratori',
            openOnNewTab: true,
          },
          {
            key: 'open-intercom',
            label: t('common:chat_with_us'),
            description: 'opens Intercom widget to let user chat the CS team',
          },
        ],
        secondaryNav: [...loggedInAvatarItems],
      },
      nonLoggedIn: {
        primaryNav: primaryNavItems,
        secondaryNav: [],
      },
    }),
    [t, employerPlatformItem, loggedInAvatarItems, primaryNavItems, socialResponsibilityItem]
  ) as WorkerNavLinksType;
};
